import {
  createContext,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import qs from 'qs';
import { ID, QueryResponseContextProps, QueryState } from './models';

function createResponseContext<T>(initialState: QueryResponseContextProps<T>) {
  return createContext(initialState);
}

function isNotEmpty(obj: unknown) {
  return obj !== undefined && obj !== null && obj !== '';
}

// Example: page=1&limit=10&sort=id&order=desc&q=a&filter_name=a&filter_online=false
function stringifyRequestQuery(state: QueryState): string {
  const pagination = qs.stringify(state, {
    filter: ['page', 'limit'],
    skipNulls: true,
  });
  const sortField = state.sort ? `sort=${state.sort}` : '';
  const sortOrder = state.order ? `sortOrder=${state.order}` : '';
  const sort = [sortField, sortOrder].filter((f) => f).join('&');

  const apiSort = qs.stringify(state, {
    filter: ['apiSort', 'apiOrder'],
    skipNulls: true,
  });

  const sortSummaryReport = qs.stringify(state, {
    filter: ['sortSummaryReport', 'orderSummaryReport'],
    skipNulls: true,
  });

  const q = isNotEmpty(state.q)
    ? qs.stringify(state, { filter: ['q'], skipNulls: true })
    : '';

  const filter = state.filter
    ? Object.entries(state.filter as object)
        .filter((obj) => isNotEmpty(obj[1]))
        .map((obj) => {
          return `${obj[0]}=${obj[1]}`;
        })
        .join('&')
    : '';

  return [pagination, sort, q, filter, apiSort, sortSummaryReport]
    .filter((f) => f)
    .join('&');
  // .toLowerCase();
}

function parseRequestQuery(query: string): QueryState {
  const cache: unknown = qs.parse(query);
  return cache as QueryState;
}

function calculatedGroupingIsDisabled<T>(
  isLoading: boolean,
  data: Array<T> | undefined,
): boolean {
  if (isLoading) {
    return true;
  }

  return !data || !data.length;
}

function calculateIsAllDataSelected<T>(
  data: Array<T> | undefined,
  selected: Array<ID>,
): boolean {
  if (!data) {
    return false;
  }

  return data.length > 0 && data.length === selected.length;
}

function groupingOnSelect(id: ID, selected: Array<ID>, setSelected: any) {
  if (!id) {
    return;
  }

  if (selected.includes(id)) {
    setSelected(selected.filter((itemId) => itemId !== id));
  } else {
    const updatedSelected = [...selected];
    updatedSelected.push(id);
    setSelected(updatedSelected);
  }
}

function groupingOnSelectAll<T>(
  isAllSelected: boolean,
  setSelected: any,
  data?: any[],
) {
  if (isAllSelected) {
    setSelected([]);
    return;
  }

  if (!data || !data.length) {
    return;
  }

  setSelected(data.filter((item) => item._id).map((item) => item._id));
}

function groupingOnSelectAllVideoCollection<T>(
  isAllSelected: boolean,
  setSelected: any,
  data?: any[],
) {
  if (isAllSelected) {
    setSelected([]);
    return;
  }

  if (!data || !data.length) {
    return;
  }

  setSelected(
    data.filter((item) => item.video_id).map((item) => item.video_id),
  );
}

// Hook
function useDebounce(value: string | undefined, delay: number) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay], // Only re-call effect if value or delay changes
  );
  return debouncedValue;
}

function secondsToHHMMSS(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const formattedHours = hours.toString().padStart(2, '0');
  const formattedMinutes = minutes.toString().padStart(2, '0');
  const formattedSeconds = remainingSeconds.toString().padStart(2, '0');

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
}

function getMonthName(monthNumber: number): string {
  // Create a new Date object with the specified month number and a day of 1
  const date = new Date(2000, monthNumber - 1, 1);
  // Use the toLocaleString method with the month option to get the month name
  return date.toLocaleString('default', { month: 'long' });
}

function dateToDDMMYY(dateStr: string) {
  const date = new Date(dateStr);

  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Months are zero-based
  const year = String(date.getUTCFullYear()).slice(-2);

  const formattedDate = `${day}${month}${year}`;
  return formattedDate;
}

export {
  dateToDDMMYY,
  createResponseContext,
  stringifyRequestQuery,
  parseRequestQuery,
  calculatedGroupingIsDisabled,
  calculateIsAllDataSelected,
  groupingOnSelect,
  groupingOnSelectAll,
  useDebounce,
  isNotEmpty,
  secondsToHHMMSS,
  getMonthName,
  groupingOnSelectAllVideoCollection,
};
